import _ from 'lodash';
import R from 'ramda';
import { arrayHelper } from 'utils/arrayHelper';

function removeUnchangedValues<T extends {}>({
    new: newValues,
    old: oldValues,
}: {
    new: Partial<T>;
    old: any;
}): Partial<T> {
    return R.pickBy((item, key) => {
        const oldItem: any = oldValues[key as keyof T];
        return !R.equals(item, oldItem);
    }, newValues);
}

function mapEntries<
    T extends { [key: string]: any },
    R extends { [key: string]: any } = Record<keyof any, any>
>(pred: (i: [keyof T, T[keyof T]]) => [keyof any, R[keyof R]]): (obj: T) => R;
function mapEntries<T extends {}, R extends {} = Record<keyof any, any>>(
    pred: (i: [keyof T, T[keyof T]]) => [keyof any, R[keyof R]],
    obj: T,
): R;
function mapEntries(...args: any[]) {
    return R.curry((pred: any, obj: any) => {
        return Object.fromEntries(Object.entries(obj).map(pred));
        // @ts-ignore
    })(...args);
}

const filterEntries = <T extends {}>(
    pred: (i: [string, any]) => boolean,
    obj: T,
): T => {
    return Object.fromEntries(Object.entries(obj).filter(pred)) as T;
};

type Excluder = string | RegExp;
export type KeyTransformerOptions = {
    deep?: boolean;
    excludeKeys?: Excluder | Excluder[];
    excludeChildrenOfKeys?: Excluder | Excluder[];
};

type KeyTransformer = (key: string, options?: KeyTransformerOptions) => string;
const transformKeys = <T>(
    transformer: KeyTransformer,
    obj: T,
    {
        deep = true,
        excludeChildrenOfKeys = [],
        excludeKeys = [],
    }: KeyTransformerOptions = {},
) => {
    if (!_.isObject(obj)) {
        return obj;
    }

    const options = { deep, excludeChildrenOfKeys, excludeKeys };

    return mapEntries((item) => {
        const [key, value] = item as [string, any];
        const isExcludingKey = checkIsExcluding(excludeKeys, key);
        const newKey = isExcludingKey ? key : transformer(key, options);

        let newValue = value;

        const shouldChangeChildren = !checkIsExcluding(
            excludeChildrenOfKeys,
            key,
        );
        if (deep && shouldChangeChildren && _.isObject(value)) {
            if (Array.isArray(value)) {
                newValue = value.map((subObj) =>
                    transformKeys(transformer, subObj, options),
                );
            } else {
                newValue = transformKeys(transformer, value, options);
            }
        }

        return [newKey, newValue];
    }, obj);
};

function checkIsExcluding(excluders: Excluder | Excluder[], key: string) {
    return arrayHelper.singleToArray(excluders).some((excluder) => {
        if (typeof excluder === 'string') {
            return excluder === key;
        }
        return excluder.test(key);
    });
}

const snakeCaseKeys = (obj: object, options?: KeyTransformerOptions) =>
    transformKeys(_.snakeCase, obj, options);
const camelCaseKeys = (obj: object, options?: KeyTransformerOptions) =>
    transformKeys(_.camelCase, obj, options);

export const objectHelper = {
    transformKeys,
    snakeCaseKeys,
    camelCaseKeys,
    removeUnchangedValues,
    mapEntries,
    filterEntries,
};
